import { gql } from '@apollo/client';
import { getDateWithTimezoneFixed, readableDate, subscribe, TConnectionItem } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { startOfDay, startOfMonth } from 'date-fns';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { client } from '../../config/api';
import { onSubscriptionData } from '../../config/cache';
import {
  BaseGiftFieldsFragmentDoc,
  GetGiftDocument,
  GiftEventFieldsFragmentDoc,
  GiftFieldsFragmentDoc,
  OnAnyEventGiftCreatedDocument,
  OnAnyGiftChangedDocument,
  OnAnyGiftRemovedFromGroupDocument,
  OnAnyShipmentRecreatedDocument,
  TAddressFieldsFragment,
  TGetGiftQuery,
  TGetGiftQueryVariables,
  TGiftFieldsFragment,
  TGiftNewFieldsFragment,
  TGiftNewFieldsWithShipmentFragment,
  TGiftTypes,
  TListEmployeeAndDependatsGiftsQuery,
  TListEmployeeAndDependatsGiftsQueryVariables,
  TListGiftsAssemblingQuery,
  TListGiftsAssemblingQueryVariables,
  TListNotDoneGiftsQuery,
  TListNotDoneGiftsQueryVariables,
  TListOneGiftQuery,
  TListOneGiftQueryVariables,
  TListOneShipmentGiftsQuery,
  TListOneShipmentGiftsQueryVariables,
  TListRecentlyDoneGiftsQuery,
  TListRecentlyDoneGiftsQueryVariables,
  TListShipmentGiftsQuery,
  TListShipmentGiftsQueryVariables,
  TOnAnyGiftChangedSubscription,
  TOnAnyGiftChangedSubscriptionVariables,
  useGetGiftLazyQuery,
  useListEmployeeAndDependatsGiftsLazyQuery,
  useListGiftsAssemblingLazyQuery,
  useListNotDoneGiftsLazyQuery,
  useListOneGiftLazyQuery,
  useListOneShipmentGiftsLazyQuery,
  useListRecentlyDoneGiftsLazyQuery,
  useListShipmentGiftsLazyQuery
} from '../../generated/graphql';
import { TAddressType, useAllAddressesMap } from '../addresses/queries';
import { useAllCompaniesMapNEW } from '../companies/queries';
import { TDependantType, useAllDependantsMap } from '../dependants/queries';
import { TEmployeeType, useAllEmployeesMap } from '../employees/queries';
import { useSubscribeToEventChanges } from '../events/queries';
import { calculatedShippingDate } from '../gifts/shared';
import { TOfficeType } from '../office/queries';
import { useFragmentOrFetch } from '../shared/useFragmentOrFetch';
import { useQueryAll } from '../shared/useQueryAll';

const GiftFieldsWithDependenciesFragmentDoc = gql`
  ${GiftFieldsFragmentDoc}
  ${BaseGiftFieldsFragmentDoc}
  ${GiftEventFieldsFragmentDoc}
`;

type TGiftChangeData = {
  onAnyGiftChanged?: Pick<TGiftFieldsFragment, 'id' | 'companyId' | '_deleted'> | null;
};

export const onGiftSubscriptionData = async (data?: TGiftChangeData, vars?: TOnAnyGiftChangedSubscriptionVariables) => {
  const { companyId, id, _deleted } = data?.onAnyGiftChanged || {};

  if (_deleted) return onSubscriptionData(data as Record<string, any>, vars);
  if (!companyId || !id) return;

  const response = await client.query<TGetGiftQuery, TGetGiftQueryVariables>({
    query: GetGiftDocument,
    variables: {
      id,
      companyId
    }
  });

  const giftData = response.data.getGift;

  if (giftData) {
    const subscriptionData = {
      onAnyGiftChanged: giftData as TConnectionItem
    };

    // CHANGED DATA FOR QUERIES THAT DEPEND ON NO VARIABLE (SINCE VARS SHOULD BE AN EMPTY OBJECT IN THIS SITUATION)
    onSubscriptionData(subscriptionData, vars);

    // CHANGED DATA FOR QUERIES THAT DEPEND ON SHIPMENT ID AND COMPANY ID
    if (giftData.shipmentId && giftData.companyId)
      onSubscriptionData(subscriptionData, {
        shipmentId: giftData.shipmentId,
        companyId: giftData.companyId
      });
  }
};

let giftChangedSubscribed = false;
let eventGiftCreatedSubscribed = false;
let giftRemovedFromGroupSubscribed = false;
let recreateShipmentSubscribed = false;

export const useSubscribeToGiftChanged = () => {
  useSubscribeToEventChanges();

  useEffect(() => {
    if (!giftChangedSubscribed) {
      subscribe({
        client,
        query: OnAnyGiftChangedDocument,
        onSubscriptionData: (data, vars) => {
          return onGiftSubscriptionData(data as TOnAnyGiftChangedSubscription, vars as TOnAnyGiftChangedSubscriptionVariables);
        }
      });

      giftChangedSubscribed = true;
    }
  }, []);

  useEffect(() => {
    if (!eventGiftCreatedSubscribed) {
      subscribe({
        client,
        query: OnAnyEventGiftCreatedDocument,
        onSubscriptionData: async (data, vars) => {
          const { gift } = data?.onAnyEventGiftCreated || {};
          await onGiftSubscriptionData(
            {
              onAnyGiftChanged: gift
            },
            vars as TOnAnyGiftChangedSubscriptionVariables
          );
        }
      });

      eventGiftCreatedSubscribed = true;
    }
  });

  useEffect(() => {
    if (!giftRemovedFromGroupSubscribed) {
      subscribe({
        client,
        query: OnAnyGiftRemovedFromGroupDocument,
        onSubscriptionData: async (data, vars) => {
          const { items } = data?.onAnyGiftRemovedFromGroup || {};

          if (items && Array.isArray(items) && items.length > 0)
            for (const item of items) {
              await onGiftSubscriptionData(
                {
                  onAnyGiftChanged: item
                },
                vars as TOnAnyGiftChangedSubscriptionVariables
              );
            }
        }
      });

      giftRemovedFromGroupSubscribed = true;
    }
  }, []);

  useEffect(() => {
    if (!recreateShipmentSubscribed) {
      subscribe({
        client,
        query: OnAnyShipmentRecreatedDocument,
        onSubscriptionData: async (data, vars) => {
          const { items } = data?.onAnyShipmentRecreated || {};

          if (items && Array.isArray(items) && items.length > 0)
            for (const item of items) {
              await onGiftSubscriptionData(
                {
                  onAnyGiftChanged: item
                },
                vars as TOnAnyGiftChangedSubscriptionVariables
              );
            }
        }
      });

      recreateShipmentSubscribed = true;
    }
  }, []);
};

// LIST

export const getShippingDate = (gift: TGiftFieldsFragment & { address?: TAddressFieldsFragment }, referenceEventDate: string | Date) => {
  return calculatedShippingDate({
    date: referenceEventDate,
    address: { country: gift.address?.country as string, state: gift.address?.state as string }
  });
};

export const useFullGifts = <T extends TGiftFieldsFragment>(gifts: T[]) => {
  const { data: companiesMap, error: companiesError, loading: companiesLoading } = useAllCompaniesMapNEW();

  const { data: employeesMap, error: employeesError, loading: employeesLoading } = useAllEmployeesMap();

  const { data: dependantsMap, error: dependantsError, loading: dependantsLoading } = useAllDependantsMap();

  const { data: addressesMap, error: addressesError, loading: addressesLoading } = useAllAddressesMap();

  const fullGifts = useMemo(() => {
    return gifts.map((g) => {
      let recipient: (typeof employeesMap)[string] | (typeof dependantsMap)[string] | undefined;

      if (g.recipientId != null) {
        const [tableName, recipientId] = g.recipientId.split('|');

        if (tableName === import.meta.env.REACT_APP_DEPENDANTS_TABLE_NAME) recipient = dependantsMap[recipientId];
        else if (tableName === import.meta.env.REACT_APP_EMPLOYEES_TABLE_NAME) recipient = employeesMap[recipientId];
      }

      const giftWithRelations = {
        ...g,
        company: g.companyId ? companiesMap[g.companyId] : undefined,
        employee: g.employeeId ? employeesMap[g.employeeId] : undefined,
        recipient: recipient,
        address: g.addressId ? addressesMap[g.addressId] : undefined
      };

      return {
        ...giftWithRelations,
        shippingDate: getShippingDate(giftWithRelations, g.eventDate)
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressesMap, companiesMap, dependantsMap, employeesMap, JSON.stringify(gifts)]);

  return {
    data: fullGifts,
    error: companiesError || employeesError || dependantsError || addressesError,
    loading: companiesLoading || employeesLoading || dependantsLoading || addressesLoading
  };
};

export const useQueryAllAssemblingGifts = () => {
  useSubscribeToGiftChanged();

  const {
    data: notDoneGiftsData,
    error: notDoneError,
    loading: notDoneLoading
  } = useQueryAll<TListNotDoneGiftsQuery, TListNotDoneGiftsQueryVariables>({
    useQuery: useListNotDoneGiftsLazyQuery
  });

  const {
    data: recentlyDoneGiftsData,
    error: recentlyDoneError,
    loading: recentlyDoneLoading
  } = useQueryAll<TListRecentlyDoneGiftsQuery, TListRecentlyDoneGiftsQueryVariables>({
    useQuery: useListRecentlyDoneGiftsLazyQuery
  });

  const giftAssemblingBoardGifts = useMemo(() => {
    return _.uniqBy(
      _.compact(notDoneGiftsData?.listNotDoneGifts.items || [])
        .filter((g) => {
          if (g.isDone === 2) return true;
          else return g.doneAt == null;
        })
        .concat(
          _.compact(recentlyDoneGiftsData?.listRecentlyDoneGifts.items || []).filter((g) => {
            if (g.isDone === 2) return true;
            if (g.doneAt == null) return false;
            if (g.shipmentId) return false;
            const d = new Date(g.doneAt);
            const now = new Date();
            return new Date(now.setDate(now.getDate() - 14)).getTime() <= d.getTime();
          })
        ),
      'id'
    );
  }, [notDoneGiftsData?.listNotDoneGifts.items, recentlyDoneGiftsData?.listRecentlyDoneGifts.items]);

  const { data: fullGifts, error: fullError, loading: fullLoading } = useFullGifts<TGiftFieldsFragment>(giftAssemblingBoardGifts);

  return {
    data: fullGifts,
    error: fullError || notDoneError || recentlyDoneError,
    loading: fullLoading || notDoneLoading || recentlyDoneLoading
  };
};

export const getGiftReferenceEventDate = (gift: TGiftType): string => {
  if (gift.address?.parentType != 'employee') {
    const date = getDateWithTimezoneFixed(gift.eventDate);
    return date ? readableDate(startOfMonth(date), 'yyyy-MM-dd') : '-';
  } else if (gift.group == null) return readableDate(gift.eventDate, 'yyyy-MM-dd');
  else return readableDate((gift.groupedGifts || [gift]).sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())[0].eventDate, 'yyyy-MM-dd');
};

export const useQueryAllGroupedAssemblingGifts = () => {
  const { data, error, loading } = useQueryAllAssemblingGifts();

  const groupedGifts = useMemo(() => {
    return data.map((g) => {
      if (g.group == null) return g;
      else
        return {
          ...g,
          groupedGifts: data.filter((g1) => g1.group === g.group).sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
        };
    });
  }, [data]);

  const gifts = useMemo(() => {
    return groupedGifts
      .map((gift) => {
        const g = gift as TGiftType;
        const referenceEventDate = getGiftReferenceEventDate(g);
        const calculatedShippingDate = getShippingDate(g, referenceEventDate);
        let shippingDate: string | Date = calculatedShippingDate;

        const date = getDateWithTimezoneFixed(calculatedShippingDate);
        if (date && date < startOfDay(new Date())) shippingDate = new Date();

        return {
          ...g,
          referenceEventDate,
          shippingDate: readableDate(shippingDate, 'yyyy-MM-dd'),
          calculatedShippingDate: calculatedShippingDate
        };
      })
      .sort((a, b) => {
        const referenceEventDateDiff = new Date(a.referenceEventDate).getTime() - new Date(b.referenceEventDate).getTime();
        if (referenceEventDateDiff !== 0) return referenceEventDateDiff;

        const groupDiff = (a.group || '').localeCompare(b.group || '');
        if (groupDiff !== 0) return groupDiff;

        const eventDateDiff = new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
        if (eventDateDiff !== 0) return eventDateDiff;

        return a.id.localeCompare(b.id);
      });
  }, [groupedGifts]);

  return {
    data: gifts,
    error,
    loading
  };
};

export const useQueryAllSubscriptionAssemblingGifts = () => {
  const { data, error, loading } = useQueryAllGroupedAssemblingGifts();

  const gifts = useMemo(() => {
    const dataFilter = data.filter((g) => g.type === TGiftTypes.Birthday || g.type === TGiftTypes.WorkAnniversary);
    return _.orderBy(dataFilter, ['eventDate', 'recipient.fullName'], ['desc', 'asc']);
  }, [data]);

  return {
    data: gifts,
    error,
    loading
  };
};

export const useQueryAllNewHireAssemblingGifts = () => {
  const { data, error, loading } = useQueryAllGroupedAssemblingGifts();

  const gifts = useMemo(() => {
    const dataFilter = data.filter((g) => g.type === TGiftTypes.NewHire);
    return _.orderBy(dataFilter, ['eventDate', 'recipient.fullName'], ['desc', 'asc']);
  }, [data]);

  return {
    data: gifts,
    error,
    loading
  };
};

export const useQueryAllOneOffAssemblingGifts = () => {
  const { data, error, loading } = useQueryAllGroupedAssemblingGifts();

  const gifts = useMemo(() => {
    const dataFilter = data.filter((g) => g.type !== TGiftTypes.NewHire && g.type !== TGiftTypes.WorkAnniversary && g.type !== TGiftTypes.Birthday);
    return _.orderBy(dataFilter, ['eventDate', 'recipient.fullName'], ['desc', 'asc']);
  }, [data]);

  return {
    data: gifts,
    error,
    loading
  };
};

export const useQueryAllShipmentGifts = (variables: TListShipmentGiftsQueryVariables) => {
  useSubscribeToGiftChanged();

  const { data, error, loading } = useQueryAll<TListShipmentGiftsQuery, TListShipmentGiftsQueryVariables>({
    useQuery: useListShipmentGiftsLazyQuery,
    variables
  });

  const { data: gifts, error: fullError, loading: fullLoading } = useFullGifts(_.compact(data?.listShipmentGifts.items));

  const sortedGifs = useMemo(() => {
    return gifts.sort((a, b) => {
      return new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
    });
  }, [gifts]);

  return {
    data: sortedGifs,
    error: error || fullError,
    loading: loading || fullLoading
  };
};

export const useQueryAllEmployeeAndDependantsGifts = (variables: TListEmployeeAndDependatsGiftsQueryVariables) => {
  useSubscribeToGiftChanged();

  const { data, error, loading } = useQueryAll<TListEmployeeAndDependatsGiftsQuery, TListEmployeeAndDependatsGiftsQueryVariables>({
    useQuery: useListEmployeeAndDependatsGiftsLazyQuery,
    variables
  });

  const { data: gifts, error: fullError, loading: fullLoading } = useFullGifts(_.compact(data?.listEmployeeAndDependatsGifts.items));

  const filteredGifts = useMemo(() => {
    return gifts.filter((g) => g.employeeId === variables.employeeId && g.companyId === variables.companyId);
  }, [gifts, variables.companyId, variables.employeeId]);

  return {
    data: filteredGifts,
    error: error || fullError,
    loading: loading || fullLoading
  };
};

// ITEMS

export const useFragmentOrFetchGift = (variables: TGetGiftQueryVariables) => {
  useSubscribeToGiftChanged();

  const { data, error, loading } = useFragmentOrFetch<TGiftFieldsFragment, TGetGiftQuery, TGetGiftQueryVariables, TOnAnyGiftChangedSubscriptionVariables>({
    useLazyQuery: useGetGiftLazyQuery,
    variables,
    fragmentDoc: GiftFieldsWithDependenciesFragmentDoc,
    __typename: 'Gift'
  });

  const { data: gifts, error: fullError, loading: fullLoading } = useFullGifts(_.compact([data]) as TGiftFieldsFragment[]);

  return {
    data: gifts[0],
    error: error || fullError,
    loading: loading || fullLoading
  };
};

// =========================
// NEW
// =========================

export type TGiftType = TGiftNewFieldsWithShipmentFragment & {
  company?: Record<string, any>;
  office?: TOfficeType;
  employee?: TEmployeeType;
  address?: TAddressType;
  recipient?: TEmployeeType | TDependantType;
  groupedGifts?: TGiftNewFieldsFragment[];
};

export const useQueryListGiftsAssembling = ({ type }: TListGiftsAssemblingQueryVariables) => {
  useSubscribeToGiftChanged();
  // NOT DONE Gifts
  const {
    data: notDoneGifts,
    error: notDoneError,
    loading: notDoneLoading
  } = useQueryAll<TListGiftsAssemblingQuery, TListGiftsAssemblingQueryVariables>({
    useQuery: useListGiftsAssemblingLazyQuery,
    variables: {
      type: type,
      isDone: 0,
      limit: 99
    }
  });
  // RECENTLY DONE Gifts
  const {
    data: recentlyDoneGifts,
    error: recentlyDoneError,
    loading: recentlyDoneLoading
  } = useQueryAll<TListGiftsAssemblingQuery, TListGiftsAssemblingQueryVariables>({
    useQuery: useListGiftsAssemblingLazyQuery,
    variables: {
      type: type,
      isDone: 2,
      limit: 99
    }
  });
  // Combine
  const gifts = useMemo(() => [...(notDoneGifts?.listGiftsAssembling.items || []), ...(recentlyDoneGifts?.listGiftsAssembling.items || [])], [notDoneGifts, recentlyDoneGifts]);

  return {
    data: gifts,
    error: notDoneError || recentlyDoneError,
    loading: notDoneLoading || recentlyDoneLoading
  };
};

export const useQueryAllSubscriptionAssemblingGiftsNew = () => {
  const {
    data: birthdayGifts,
    error: errorBirthdayGifts,
    loading: loadingBirthdayGifts
  } = useQueryListGiftsAssembling({
    type: 'birthday'
  });

  const {
    data: workAnniversaryGifts,
    error: errorWorkAnniversaryGifts,
    loading: loadingWorkAnniversaryGifts
  } = useQueryListGiftsAssembling({
    type: 'workAnniversary'
  });

  const gifts = useMemo(() => _.orderBy([...(birthdayGifts || []), ...(workAnniversaryGifts || [])], ['eventDate', 'recipient.fullName'], ['desc', 'asc']), [birthdayGifts, workAnniversaryGifts]);

  return {
    data: gifts as unknown as TGiftType[],
    error: errorBirthdayGifts || errorWorkAnniversaryGifts,
    loading: loadingBirthdayGifts || loadingWorkAnniversaryGifts
  };
};

export const useQueryAllNewHireAssemblingGiftsNew = () => {
  const {
    data: newHire,
    error: errorNewHire,
    loading: loadingNewHire
  } = useQueryListGiftsAssembling({
    type: 'newHire'
  });

  const gifts = useMemo(() => _.orderBy([...(newHire || [])], ['eventDate', 'recipient.fullName'], ['desc', 'asc']), [newHire]);

  return {
    data: gifts as unknown as TGiftType[],
    error: errorNewHire,
    loading: loadingNewHire
  };
};

export const useQueryAllOneOffAssemblingGiftsNew = () => {
  const {
    data: babyGifts,
    error: babyError,
    loading: babyLoading
  } = useQueryListGiftsAssembling({
    type: 'babyGift'
  });

  const {
    data: bereavementGifts,
    error: bereavementError,
    loading: bereavementLoading
  } = useQueryListGiftsAssembling({
    type: 'bereavement'
  });

  const {
    data: weddingGifts,
    error: weddingError,
    loading: weddingLoading
  } = useQueryListGiftsAssembling({
    type: 'wedding'
  });

  const {
    data: replacementWAGifts,
    error: replacementWAError,
    loading: replacementWALoading
  } = useQueryListGiftsAssembling({
    type: 'replacementWorkAnniversary'
  });

  const {
    data: replacementEBGifts,
    error: replacementEBError,
    loading: replacementEBLoading
  } = useQueryListGiftsAssembling({
    type: 'replacementEmployeeBirthday'
  });

  const {
    data: careGifts,
    error: careError,
    loading: careLoading
  } = useQueryListGiftsAssembling({
    type: 'carePackage'
  });

  const {
    data: customBricksGifts,
    error: customBricksError,
    loading: customBricksLoading
  } = useQueryListGiftsAssembling({
    type: 'customBricks'
  });

  const {
    data: otherGifts,
    error: otherError,
    loading: otherLoading
  } = useQueryListGiftsAssembling({
    type: 'other'
  });

  const gifts = useMemo(
    () =>
      _.orderBy(
        [...(babyGifts || []), ...(bereavementGifts || []), ...(weddingGifts || []), ...(replacementWAGifts || []), ...(replacementEBGifts || []), ...(careGifts || []), ...(customBricksGifts || []), ...(otherGifts || [])],
        ['eventDate', 'recipient.fullName'],
        ['desc', 'asc']
      ),
    [babyGifts, bereavementGifts, weddingGifts, replacementWAGifts, replacementEBGifts, careGifts, customBricksGifts, otherGifts]
  );

  return {
    data: gifts as unknown as TGiftType[],
    error: babyError || bereavementError || weddingError || replacementWAError || replacementEBError || careError || customBricksError || otherError,
    loading: babyLoading || bereavementLoading || weddingLoading || replacementWALoading || replacementEBLoading || careLoading || customBricksLoading || otherLoading
  };
};

export const useQueryOneGift = (variables: TListOneGiftQueryVariables) => {
  useSubscribeToGiftChanged();

  const { data, error, loading } = useQueryAll<TListOneGiftQuery, TListOneGiftQueryVariables>({
    useQuery: useListOneGiftLazyQuery,
    variables: { ...variables, limit: 1 }
  });

  const gift = useMemo(() => data?.listOneGift.items[0] || {}, [data]);

  return {
    data: gift as TGiftType,
    error,
    loading
  };
};

export const useQueryOneShipmentGifts = (variables: TListOneShipmentGiftsQueryVariables) => {
  useSubscribeToGiftChanged();

  const { data, error, loading } = useQueryAll<TListOneShipmentGiftsQuery, TListOneShipmentGiftsQueryVariables>({
    useQuery: useListOneShipmentGiftsLazyQuery,
    variables: { ...variables, limit: 99 }
  });

  const gifts = useMemo(() => data?.listOneShipmentGifts.items || [], [data]);

  return {
    data: gifts as unknown as TGiftType[],
    error,
    loading
  };
};

export const useQueryAllNoShipmentGifts = ({ type }: TListGiftsAssemblingQueryVariables) => {
  useSubscribeToGiftChanged();
  // Gifts with no shipmentId
  const {
    data: orphanGifts,
    error: orphanErrors,
    loading: orphanLoading
  } = useQueryAll<TListGiftsAssemblingQuery, TListGiftsAssemblingQueryVariables>({
    useQuery: useListGiftsAssemblingLazyQuery,
    variables: {
      type: type,
      isDone: 1,
      filter: {
        FilterExpression: '(attribute_not_exists(shipmentId) OR attribute_exists(shipmentDeleted))',
        ExpressionAttributeNames: [],
        ExpressionAttributeValues: []
      },
      limit: 99
    }
  });
  // Combine
  //const gifts = useMemo(() => [...(orphanGifts.listGiftsAssembling.items || [])], [orphanGifts]);
  const gifts = useMemo(() => {
    return [...(orphanGifts?.listGiftsAssembling.items || [])];
    //return _.orderBy(newGifts, ['eventDate', 'recipient.fullName'], ['desc', 'asc']);
  }, [orphanGifts]);

  return {
    data: gifts,
    error: orphanErrors,
    loading: orphanLoading
  };
};

export const useQueryOrphanGifts = () => {
  const {
    data: birthdayGifts,
    error: errorBirthdayGifts,
    loading: loadingBirthdayGifts
  } = useQueryAllNoShipmentGifts({
    type: 'birthday'
  });

  const {
    data: workAnniversaryGifts,
    error: errorWorkAnniversaryGifts,
    loading: loadingWorkAnniversaryGifts
  } = useQueryAllNoShipmentGifts({
    type: 'workAnniversary'
  });

  const {
    data: newHire,
    error: errorNewHire,
    loading: loadingNewHire
  } = useQueryAllNoShipmentGifts({
    type: 'newHire'
  });

  const {
    data: babyGifts,
    error: babyError,
    loading: babyLoading
  } = useQueryAllNoShipmentGifts({
    type: 'babyGift'
  });

  const {
    data: weddingGifts,
    error: weddingError,
    loading: weddingLoading
  } = useQueryAllNoShipmentGifts({
    type: 'wedding'
  });

  const {
    data: replacementWAGifts,
    error: replacementWAError,
    loading: replacementWALoading
  } = useQueryAllNoShipmentGifts({
    type: 'replacementWorkAnniversary'
  });

  const {
    data: replacementEBGifts,
    error: replacementEBError,
    loading: replacementEBLoading
  } = useQueryAllNoShipmentGifts({
    type: 'replacementEmployeeBirthday'
  });

  const {
    data: careGifts,
    error: careError,
    loading: careLoading
  } = useQueryAllNoShipmentGifts({
    type: 'carePackage'
  });

  const {
    data: customBricksGifts,
    error: customBricksError,
    loading: customBricksLoading
  } = useQueryAllNoShipmentGifts({
    type: 'customBricks'
  });

  const {
    data: otherGifts,
    error: otherError,
    loading: otherLoading
  } = useQueryAllNoShipmentGifts({
    type: 'other'
  });

  const gifts = useMemo(
    () => [
      ...(birthdayGifts || []),
      ...(workAnniversaryGifts || []),
      ...(newHire || []),
      ...(babyGifts || []),
      ...(weddingGifts || []),
      ...(replacementWAGifts || []),
      ...(replacementEBGifts || []),
      ...(careGifts || []),
      ...(customBricksGifts || []),
      ...(otherGifts || [])
    ],
    [birthdayGifts, workAnniversaryGifts, newHire, babyGifts, weddingGifts, replacementWAGifts, replacementEBGifts, careGifts, customBricksGifts, otherGifts]
  );

  return {
    data: gifts as unknown as TGiftType[],
    error: errorBirthdayGifts || errorWorkAnniversaryGifts || errorNewHire || babyError || weddingError || replacementWAError || replacementEBError || careError || customBricksError || otherError,
    loading: loadingBirthdayGifts || loadingWorkAnniversaryGifts || loadingNewHire || babyLoading || weddingLoading || replacementWALoading || replacementEBLoading || careLoading || customBricksLoading || otherLoading
  };
};
